import React, { useCallback } from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button } from '@picsio/ui';

import {
  formatPublishErrorFieldName,
  formatPublishErrorValue,
} from '../../../store/products/helpers/formatPublishErrorDisplay';
import localization from '../../../shared/strings';
import Icon from '../../Icon';
import styles from './styles.module.scss';

const METAFIELD_MAPPING_MISSING = 'metafield_mapping_missing';
const METAFIELD_VALIDATION_FAILED = 'metafield_validation_failed';

const renderPublishFieldError = (error, strings) => {
  const isMetafieldReason = error.reason === METAFIELD_MAPPING_MISSING
    || error.reason === METAFIELD_VALIDATION_FAILED;
  const fieldName = isMetafieldReason
    ? formatPublishErrorFieldName(error.fieldName)
    : error.fieldName;

  if (error.reason === METAFIELD_MAPPING_MISSING && fieldName) {
    return strings.ERROR_MAPPING(fieldName, styles);
  }

  if (error.reason === METAFIELD_VALIDATION_FAILED && fieldName) {
    const currentValue = formatPublishErrorValue(error.currentValue);
    if (currentValue != null && currentValue !== '') {
      return strings.ERROR_VALIDATION(fieldName, currentValue, error.message, styles);
    }
  }

  return (
    <span className={styles.errorText}>
      <If condition={fieldName}>
        <span className={styles.errorFieldName}>{fieldName}: </span>
      </If>
      {error.message}
    </span>
  );
};

const PublishFailedDialog = ({
  errors,
  mode = 'error',
  onClose,
  root,
}) => {
  const strings = mode === 'warning'
    ? localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.PUBLISH_VARIANT_MEDIA_WARNING
    : localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.PUBLISH_FAILED;

  const destroy = useCallback(() => {
    try {
      root.unmount();
    } catch {
      // ignore
    }
  }, [root]);

  const handleClose = useCallback(() => {
    destroy();
    onClose?.();
  }, [destroy, onClose]);

  return (
    <div className={`simpleDialog ${styles.publishFailedDialog} ${mode === 'warning' ? styles.publishWarningDialog : ''}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${styles.publishFailedDialogBox}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{strings.TITLE}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleClose}
            onKeyDown={handleClose}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <p className={styles.intro}>{strings.INTRO}</p>
            <ul className={styles.errorList}>
              {errors.map((error) => (
                <li
                  key={`${error.fieldName || 'field'}-${error.message}-${error.currentValue || ''}`}
                  className={styles.errorItem}
                >
                  {renderPublishFieldError(error, strings)}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button color="secondary" size="md" variant="contained" onClick={handleClose}>
            {strings.BTN_CANCEL}
          </Button>
          <Button color="primary" size="md" variant="contained" onClick={handleClose}>
            {strings.BTN_OK}
          </Button>
        </div>
      </div>
    </div>
  );
};

PublishFailedDialog.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.shape({
    fieldName: PropTypes.string,
    message: PropTypes.string.isRequired,
    ownerType: PropTypes.string,
    reason: PropTypes.string,
    currentValue: PropTypes.string,
    variantId: PropTypes.string,
    assetId: PropTypes.string,
  })).isRequired,
  mode: PropTypes.oneOf(['error', 'warning']),
  onClose: PropTypes.func,
  root: PropTypes.shape({
    unmount: PropTypes.func.isRequired,
  }).isRequired,
};

PublishFailedDialog.defaultProps = {
  mode: 'error',
  onClose: undefined,
};

export function showPublishFailedDialog({ errors, mode = 'error', onClose }) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <PublishFailedDialog
      errors={errors}
      mode={mode}
      root={root}
      onClose={onClose}
    />,
  );
}

export default PublishFailedDialog;
